import { Typography, Grid, ToggleButtonGroup, ToggleButton } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import MoneyIcon from '@mui/icons-material/Money';
import FunctionsIcon from '@mui/icons-material/Functions';
import { LineChart, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, Label } from 'recharts';
import { WidgetPanel } from './WidgetPanel';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { gql } from '../__generated__';
import { useQuery } from '@apollo/client/react';
import { useState } from 'react';

const GET_USER_COUNT_TREND = gql(`
  query GetUserCountTrend($showCount: Boolean!, $showAcc: Boolean!) {
    trend: getUserTrend(showCount: $showCount, showAcc: $showAcc) {
      intervalName
      values {
        key
        name
        value
      }
    }
  }
`);

const lineColors = ['#71B1CC', '#287891', '#52823b', '#81710a', '#1181C4', '#7D5C65', '#B85C38', '#6A4C93'];

export function UserTrend() {
  const [count, setCount] = useState(true);
  const [acc, setAcc] = useState(true);

  const handleChange = (event: React.MouseEvent<HTMLElement>, cnt: boolean) => {
    if (cnt != null) setCount(cnt);
  };

  const { loading, data } = useQuery(GET_USER_COUNT_TREND, { variables: { showCount: count, showAcc: acc }, fetchPolicy: 'network-only' });
  const lines = data?.trend?.[0]?.values?.map((x) => ({ key: x!.key!, name: x!.name! })) ?? [];
  const rows = data?.trend?.map((month) => ({
    intervalName: month!.intervalName,
    ...Object.fromEntries((month!.values ?? []).map((x) => [x!.key, x!.value])),
  }));

  const LinkToDetails = (props) => (
    <Link to={`/report?tab=0&offset=${props.offset}`} title={props.title}>
      {props.children}
    </Link>
  );

  return (
    <div style={{ maxWidth: 800 }}>
      <WidgetPanel>
        <Grid container>
          <Grid>
            <Typography variant="h5" gutterBottom>
              <span>Brukerutvikling siste 12 måneder</span>
            </Typography>
          </Grid>
        </Grid>

        {loading && <LoadingSpinner />}
        {!loading && !data && <div>Mangler data</div>}
        {data && (
          <div>
            <ResponsiveContainer width="100%" height={310}>
              <LineChart
                width={550}
                height={300}
                data={rows}
                margin={{
                  top: 5,
                  right: 10,
                  left: 20,
                  bottom: 5,
                }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="intervalName" />
                <YAxis
                  type="number"
                  name={count ? 'antall' : 'beløp'}
                  width={count ? 60 : 90}
                  tickFormatter={(tick) => (tick ? tick.toLocaleString('nb-NO') : '')}>
                  <Label
                    style={{ fontSize: 14, color: 'grey' }}
                    value={count ? 'antall' : 'beløp'}
                    position="left"
                    angle={-90}
                    offset={!count ? 10 : 0}
                  />
                </YAxis>
                <Tooltip />
                <Legend />
                {lines.map((line, index) => (
                  <Line
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    name={line.name}
                    stroke={lineColors[index % lineColors.length]}
                    activeDot={index === 0 ? { r: 8 } : undefined}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <Grid container direction="row" sx={{justifyContent: 'space-between', alignItems: 'center'}}>
          <Grid>
            <LinkToDetails>Se mer</LinkToDetails>
          </Grid>
          <Grid>
            <ToggleButtonGroup size="small" value={count} onChange={handleChange} exclusive>
              <ToggleButton value={true} aria-label="brukere" title="brukere">
                <PeopleIcon></PeopleIcon>
              </ToggleButton>
              <ToggleButton value={false} aria-label="beløp" title="beløp">
                <MoneyIcon></MoneyIcon>
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButton
              sx={{
                verticalAlign: 'baseline',
                marginLeft: 5,
              }}
              size="small"
              value="check"
              selected={acc}
              onChange={() => {
                setAcc(!acc);
              }}
              aria-label="netto/akkumulert"
              title="akkumulert">
              <FunctionsIcon></FunctionsIcon>
            </ToggleButton>
          </Grid>
        </Grid>
      </WidgetPanel>
    </div>
  );
}
